import { FC, ReactNode } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PublicIcon from "@mui/icons-material/Public";
import GroupIcon from "@mui/icons-material/Group";
import BusinessIcon from "@mui/icons-material/Business";
import LanguageIcon from "@mui/icons-material/Language";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BarChartIcon from "@mui/icons-material/BarChart";
import CategoryIcon from "@mui/icons-material/Category";
import HistoryIcon from "@mui/icons-material/History";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PaidIcon from "@mui/icons-material/Paid";
import CloudIcon from "@mui/icons-material/Cloud";
import WifiTetheringIcon from "@mui/icons-material/WifiTethering";
import RouteIcon from "@mui/icons-material/Route";
import PlaceIcon from "@mui/icons-material/Place";
import { type CriterionKey } from "@/lib/scoring/types";
import { CRITERION_REGISTRY } from "@/lib/scoring/criterionRegistry";
import { useFeatureFlag } from "@/hooks/useLaunchDarkly";

interface PickerItem {
  key: string;
  label: string;
  icon: ReactNode;
  enabled: boolean;
}

const PICKER_GROUPS: Array<{ label: string; items: PickerItem[] }> = [
  {
    label: "Firmographics",
    items: [
      {
        key: "geography",
        label: "Geography",
        icon: <PublicIcon />,
        enabled: true,
      },
      {
        key: "globalPresence",
        label: "Global Presence",
        icon: <LanguageIcon />,
        enabled: true,
      },
      {
        key: "industry",
        label: "Industry",
        icon: <BusinessIcon />,
        enabled: true,
      },
      {
        key: "employees",
        label: "Employees",
        icon: <GroupIcon />,
        enabled: true,
      },
      {
        key: "revenue",
        label: "Revenue",
        icon: <AttachMoneyIcon />,
        enabled: true,
      },
    ],
  },
  {
    label: "Tech Install",
    items: [
      {
        key: "products",
        label: "Product Presence",
        icon: <CheckCircleOutlineIcon />,
        enabled: true,
      },
      {
        key: "vendorPenetration",
        label: "Vendor Penetration",
        icon: <BarChartIcon />,
        enabled: true,
      },
      {
        key: "productCategoryPenetration",
        label: "Products in a Category",
        icon: <CategoryIcon />,
        enabled: true,
      },
      {
        key: "productInstallAgeMonths",
        label: "Product Install Age",
        icon: <HistoryIcon />,
        enabled: true,
      },
      {
        key: "productIntensity",
        label: "Product Intensity",
        icon: <TrendingUpIcon />,
        enabled: true,
      },
    ],
  },
  {
    label: "IT Spend",
    items: [
      {
        key: "spend",
        label: "IT Spend Amount",
        icon: <PaidIcon />,
        enabled: false,
      },
    ],
  },
  {
    label: "Cloud",
    items: [
      {
        key: "cloudMaturity",
        label: "Cloud Maturity",
        icon: <CloudIcon />,
        enabled: false,
      },
    ],
  },
  {
    label: "Intent",
    items: [
      {
        key: "signalStrength",
        label: "Signal Strength",
        icon: <WifiTetheringIcon />,
        enabled: true,
      },
      {
        key: "contextType",
        label: "Context Type",
        icon: <BarChartIcon />,
        enabled: true,
      },
      {
        key: "buyersJourney",
        label: "Buyer's Journey",
        icon: <RouteIcon />,
        enabled: true,
      },
      {
        key: "location",
        label: "Signal Location",
        icon: <PlaceIcon />,
        enabled: true,
      },
    ],
  },
];

interface CriterionPickerDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (key: CriterionKey) => void;
}

export const CriterionPickerDialog: FC<CriterionPickerDialogProps> = ({
  open,
  onClose,
  onSelect,
}) => {
  const scoringEnabled = useFeatureFlag("marketAnalyzerScoringEnabled");

  const pickerGroups = PICKER_GROUPS.map((group) => {
    if (group.label === "Cloud") {
      return {
        ...group,
        items: group.items.map((item) =>
          item.key === "cloudMaturity"
            ? { ...item, enabled: scoringEnabled }
            : item
        ),
      };
    }
    if (group.label === "IT Spend") {
      return {
        ...group,
        items: group.items.map((item) =>
          item.key === "spend" ? { ...item, enabled: scoringEnabled } : item
        ),
      };
    }
    return group;
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Scoring Type</DialogTitle>
      <DialogContent dividers>
        {pickerGroups.map((group) => (
          <Box key={group.label} sx={{ mb: 2.5 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                letterSpacing: 0.8,
                color: "text.secondary",
                display: "block",
                mb: 1,
              }}
            >
              {group.label.toUpperCase()}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {group.items.map((item) => (
                <Tooltip
                  key={item.key}
                  title={
                    CRITERION_REGISTRY[item.key as CriterionKey]
                      ?.pickerTooltip ?? ""
                  }
                  placement="top"
                >
                  <span>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={!item.enabled}
                      startIcon={item.icon}
                      data-testid={`criterion-picker-${item.key}`}
                      onClick={() => {
                        onSelect(item.key as CriterionKey);
                        onClose();
                      }}
                      sx={{
                        textTransform: "uppercase",
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: 0.5,
                      }}
                    >
                      {item.label}
                    </Button>
                  </span>
                </Tooltip>
              ))}
            </Box>
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};
